import Vue from "vue";
import Vuex from "vuex";
import router from "./router";

import dayjs from "dayjs";

import auth from "./stores/auth";
import categories from "./stores/categories";
import documents from "./stores/documents";
import documentVersions from "./stores/documentVersions";
import files from "./stores/files";
import videos from "./stores/videos";
import ideas from "./stores/ideas";
import navigation from "./stores/navigation";
import notifications from "./stores/notifications";
import userNotifications from "./stores/userNotifications";
import pageElements from "./stores/pageElements";
import pages from "./stores/pages";
import questions from "./stores/questions";
import search from "./stores/search";
import systems from "./stores/systems";
import tags from "./stores/tags";
import users from "./stores/users";
import Alerts from "./stores/alerts";
import { Forms, FormCategories } from "./stores/FormBuilder";

import { News, NewsCategories } from "./stores/news";
import { Events, Locations } from "./stores/schedule";
import { Questions, Ideas, Comments } from "./stores/collaboration";
import { InterestGroups } from "./stores/InterestGroups";
import { UserGroups } from "./stores/UserGroups";

/**
 * modules
 */
import { Banner } from "@/modules/banner/store";
import { DeviceNotifications } from "@/modules/deviceNotifications/store";
import { Surveys, SurveyCategories } from "@/modules/surveys/store"
import { Polls } from "@/modules/polls/store";

import api from "./api";

Vue.use(Vuex);

const store = new Vuex.Store({
  modules: {
    auth,
    categories,
    documents,
    documentVersions,
    files,
    InterestGroups,
    ideas,
    navigation,
    notifications,
    userNotifications,
    pageElements,
    pages,
    questions,
    search,
    systems,
    tags,
    users,
    News,
    NewsCategories,
    Events,
    Locations,
    Questions,
    Ideas,
    Comments,
    UserGroups,
    Alerts,
    Forms,
    FormCategories,
    Banner,
    DeviceNotifications,
    Surveys,
    SurveyCategories,
    Polls,
    videos
  },

  state: {
    meta: null,
    scrollPosition: null,
    panelOpen: false,
    showPanel: false,
    appLoading: false,
    fixedBanner: false,
    apiErrors: [],
    dialogs: [],
    takeover: {},
    showTakeover: true,
  },

  getters: {
    //
  },

  mutations: {
    setMeta(state, meta) {
      state.meta = meta;
    },

    showTakeover(state, val) {
      state.showTakeover = val;
    },

    setScrollPosition(state, scrollPosition) {
      state.scrollPosition = scrollPosition;
    },

    togglePanel(state) {
      state.panelOpen = !state.panelOpen;
    },

    showPanel(state) {
      state.showPanel = true;
    },

    hidePanel(state) {
      state.showPanel = false;
    },

    openPanel(state) {
      state.panelOpen = true;
    },

    closePanel(state) {
      state.panelOpen = false;
    },

    setFixedBanner(state, newState) {
      state.fixedBanner = newState;
    },

    setApiErrors(state, apiErrors) {
      state.apiErrors = apiErrors;
    },

    setDialogs(state, dialogs) {
      state.dialogs = dialogs;
    },

    addDialog(state, dialog) {
      state.dialogs.unshift(dialog);
    },
    setTakeover(state, payload) {
      state.takeover = payload;
    },
  },

  actions: {
    SET_META({ commit, state }, meta) {
      commit("setMeta", meta);
    },

    HIDE_TAKEOVER({ commit }) {
      commit("showTakeover", false);
    },

    CLOSE_PANEL({ commit, state }, url = null) {
      commit("closePanel");

      setTimeout(() => {
        commit("hidePanel");
        if (url) {
          let routerObj = { name: url };
          if (url.indexOf("/") >= 0) {
            routerObj = { path: url };
          }
          router.push(routerObj);
        } else {
          router.go(-1);
        }
      }, 600);
    },

    OPEN_PANEL({ commit, state }) {
      commit("showPanel");

      setTimeout(() => {
        commit("openPanel");
      }, 300);
    },

    createDialog({ commit, state }, dialog) {
      const timestamp = dayjs().format("YYYYMMDDHHmmssSSS");
      const { confirm, confirmText, cancelText, messageText } = dialog;

      const dialogData = {
        messageText,
        confirm,
        confirmText,
        cancelText,
        timestamp,
        isOpen: true,
      };

      commit("addDialog", dialogData);
    },
    displayApiError({ dispatch }, message) {
      dispatch(
        "notifications/create",
        {
          body: `There was a problem - ${message}`,
          type: "danger",
        },
        { root: true }
      );
    },

    async getFixedBanner({ commit }) {
      let resp;
      let payload;
      const bannerStorage = localStorage.getItem("mft_store_getFixedBanner");

      if (
        !bannerStorage ||
        JSON.parse(bannerStorage).timestamp < new Date().getTime() - 6000 * 60
      ) {
        try {
          resp = await api().get("/homepage/currentbanner");
          payload = {
            banner: resp.data.data.banner[0],
            timestamp: new Date().getTime(),
          };
          localStorage.setItem(
            "mft_store_getFixedBanner",
            JSON.stringify(payload)
          );
        } catch (err) {
          commit("setApiErrors", err);
          return Promise.reject();
        }
      } else {
        payload = JSON.parse(localStorage.getItem("mft_store_getFixedBanner"));
      }
      commit("setFixedBanner", payload.banner);
      return Promise.resolve(payload.banner);
    },

    async getTakeover({ commit }, forceRefresh = false) {
      let response;

      const localTakeover = localStorage.getItem("mft_store_getTakeover");
      let takeoverItem;
      if (
        !localTakeover ||
        forceRefresh ||
        localTakeover.timestamp < new Date().getTime() - 6000 * 60 * 24
      ) {
        try {
          response = await api().get("/homepage/takeover");
          takeoverItem = {
            alert: response.data.data.alert,
            timestamp: new Date().getTime(),
          };
          localStorage.setItem(
            "mft_store_getTakeover",
            JSON.stringify(takeoverItem)
          );
          commit("setTakeover", takeoverItem.alert);
          return Promise.resolve(takeoverItem.alert);
        } catch (err) {
          if (
            err.response.status === 404 &&
            err.response.data.data.length < 1
          ) {
            takeoverItem = {
              alert: {},
              timestamp: new Date().getTime(),
            };
            localStorage.setItem(
              "mft_store_getTakeover",
              JSON.stringify(takeoverItem)
            );
            return Promise.resolve([]);
          }
          commit("setApiErrors", err);
          return Promise.reject();
        }
      } else {
        const takeoverObj = JSON.parse(localTakeover);

        commit("setTakeover", takeoverObj.alert);
        return Promise.resolve(takeoverObj.alert);
      }
    },
  },
});

export default store;
